/**
 * Lead Query Keys
 *
 * Centralized query key factory for the leads board (LF-5701 epic).
 *
 * **Invalidation is direct, not through `query-dependencies.ts`.** That module
 * is matter-only — `getBatchDependencies` types `matterId` as required — and a
 * lead has no matter until it converts. Neither Scribe nor Intake registered
 * there either; both invalidate via `queryClient.invalidateQueries({ queryKey:
 * …Keys.all })`, and leads follows the same precedent. Use
 * `useInvalidateLeads()` (`lib/services/leads/hooks`) rather than restating the
 * key at each mutation site.
 */

import type { LeadBoardFilters } from '@/lib/services/leads/types';

export const leadKeys = {
  /** Root key — invalidating this clears every lead cache. */
  all: ['leads'] as const,

  /** Base key for every board read. Invalidating clears all filter variants. */
  boards: () => [...leadKeys.all, 'board'] as const,

  /**
   * One board read. The whole filter object is part of the key because every
   * filter changes the response *shape*, not just its contents: `outcome`
   * selects between the sectioned In-progress body and a paginated terminal
   * page (the response is bimodal — exactly one of `sections` / `page` is
   * populated), and `page` / `limit` cut a different slice of the same tab.
   */
  board: (filters?: LeadBoardFilters) =>
    [...leadKeys.boards(), filters ?? null] as const,

  /**
   * A terminal tab's **accumulated** pages (LF-5985).
   *
   * Distinct from `board()` even though the filters overlap, because the two
   * hold different shapes: `board()` caches one `LeadBoardResponse`, this one
   * caches React Query's `{ pages }` envelope as `Show 25 more` appends to it.
   * Sharing a key would let a single-page read overwrite an accumulated one and
   * silently collapse the tab back to its first 25 rows.
   *
   * `page` / `limit` are deliberately **not** part of the key — they are the
   * page parameter, so folding them in would give every page its own cache
   * entry and defeat the accumulation.
   */
  terminalPages: (filters?: Omit<LeadBoardFilters, 'page' | 'limit'>) =>
    [...leadKeys.boards(), 'pages', filters ?? null] as const,

  /**
   * Duplicate awareness at capture (LF-5987 / LEADS-20).
   *
   * Sits under `all` so `useInvalidateLeads()` clears it along with everything
   * else — after a capture the previous answer is stale by construction, since
   * the lead just created is itself now a candidate for the next enquiry.
   *
   * Both terms are part of the key: they are matched independently server-side,
   * so `{clientNames: 'Kelleher'}` and `{deceasedName: 'Kelleher'}` are
   * different questions with different answers.
   */
  duplicates: (params: {
    clientNames?: string;
    deceasedName?: string;
    email?: string;
    phone?: string;
  }) => [...leadKeys.all, 'duplicates', params] as const,

  /** Base key for single-lead reads. */
  details: () => [...leadKeys.all, 'detail'] as const,

  /**
   * One lead's detail. Published here so the surfaces that consume
   * `GET /leads/:id` share one key shape rather than each inventing its own —
   * the drift this factory exists to prevent.
   */
  detail: (leadId: number) => [...leadKeys.details(), leadId] as const,

  /**
   * The matter-side "origin lead" chip's read (LF-6021 / LEADS-50,
   * `GET /leads/by-matter/:matterId`). Keyed by matter, not by lead — this is
   * the one leads query a matter-overview surface holds.
   */
  originForMatter: (matterId: number) =>
    [...leadKeys.all, 'origin-for-matter', matterId] as const,

  /**
   * One rendered preview of a lead's Vale email (LF-6013 / LEADS-46).
   *
   * **The whole input is part of the key, deliberately.** The preview is of an
   * *unsaved* meeting, so its inputs are exactly the fields the coordinator is
   * still editing — changing the solicitor or the format rewrites the email's
   * first sentence. Keyed on the lead alone, a cached render would keep showing
   * the client a sentence that is no longer the one about to be sent.
   *
   * Sits under `all` so `useInvalidateLeads()` clears it: a firm that edits its
   * own template fork mid-session must not keep seeing the old wording.
   */
  emailPreview: (leadId: number, input: unknown) =>
    [...leadKeys.all, 'email-preview', leadId, input] as const,

  /**
   * The costs-agreement precedents the Configure step's picker offers
   * (LF-6012 / LEADS-45, `GET /leads/:id/cost-agreement/precedents`).
   *
   * **Keyed by lead even though the answer is firm-wide.** The route is scoped
   * to a lead the caller may touch — that is what puts it behind the lead's own
   * ownership guard and the `leads` gate — so the key mirrors the request rather
   * than the response, which is what keeps it invalidatable by the same rules as
   * every other lead read. The duplication across leads is a handful of cached
   * rows; a firm-keyed entry under `leadKeys` would be a second scoping story.
   *
   * Sits under `all`, so a firm that authors a new precedent mid-session gets it
   * on the next `useInvalidateLeads()`.
   */
  /**
   * Whether a booking would go out as an Outlook calendar invitation (CAL-3,
   * `GET /leads/:id/meetings/calendar-invitation`). Keyed on the solicitor too:
   * their own mailbox is the organiser chain's second rung, so picking a
   * different solicitor can change the answer.
   */
  meetingCalendarAvailability: (
    leadId: number,
    bookedSolicitorId: number | null
  ) =>
    [
      ...leadKeys.all,
      'meeting-calendar-availability',
      leadId,
      bookedSolicitorId
    ] as const,

  costAgreementPrecedents: (leadId: number) =>
    [...leadKeys.all, 'cost-agreement-precedents', leadId] as const,

  /**
   * The client-supplied documents held on one enquiry (LF-6029 / LEADS-53,
   * `GET /leads/:id/documents`).
   *
   * Its own key rather than a slice of `detail()`: the Documents card uploads
   * and removes without changing anything else about the lead, so folding it
   * into the detail entry would bounce the whole drawer — every inline field,
   * the narrative, the rail — on each dropped file. Sits under `all` so
   * `useInvalidateLeads()` still clears it.
   */
  documents: (leadId: number) => [...leadKeys.all, 'documents', leadId] as const
} as const;
